import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminExamPurchases } from "../store/admin";
import { AdminPageBody } from "../components/AdminPageShell";
import AdminNavBar from "../components/AdminNavBar";
import ExamDashboardView, { ExamRecentActivity } from "./components/examPurchases/ExamDashboardView";
import ExamSystemAlerts from "./components/examPurchases/ExamSystemAlerts";
import ExamPurchaseFilters from "./components/examPurchases/ExamPurchaseFilters";
import ExamPurchaseTable from "./components/examPurchases/ExamPurchaseTable";
import ExamPagination from "./components/examPurchases/ExamPagination";
import ExamPurchaseDetailModal from "./components/examPurchases/ExamPurchaseDetailModal";
import { EXAM_TYPE_OPTIONS } from "./components/examPurchases/examUtils";
import Toast from "../components/Toast";

const toIsoStart = (value) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const toIsoEnd = (value) =>
  value ? new Date(`${value}T23:59:59`).toISOString() : undefined;

const ExamPurchases = () => {
  const [searchParams, setSearchParams] = useSearchParams();

    const {
    examPurchases = [],
    examPurchasesLoading: loading,
    examPurchasesTotal: total,
    examPurchasesPages: totalPages,
    fetchExamPurchases,
    retryExamPurchaseFulfillment,
    examPurchaseMutationLoading: retryLoading,
    examDashboard,
    examDashboardLoading: dashboardLoading,
    fetchExamDashboard,
    fetchExamPurchaseStats,
    examPurchaseStats,
    examPurchaseStatsLoading,
  } = useAdminExamPurchases();

  // Filter state
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [paymentStatus, setPaymentStatus] = useState(searchParams.get("paymentStatus") || "");
  const [fulfillmentStatus, setFulfillmentStatus] = useState(searchParams.get("fulfillmentStatus") || "");
  const [emailStatus, setEmailStatus] = useState(searchParams.get("emailStatus") || "");
  const [startDate, setStartDate] = useState(searchParams.get("startDate") || "");
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [dashboardExamType, setDashboardExamType] = useState("");

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

    const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch purchases when filters change
  const fetchPurchases = useCallback(async () => {
    const params = {
      page,
      limit,
      search: debouncedSearch,
      sort,
      paymentStatus,
      fulfillmentStatus,
      emailStatus,
      startDate: toIsoStart(startDate),
      endDate: toIsoEnd(endDate),
    };
    const result = await fetchExamPurchases(params);
    if (!result.success) {
      showToast(result.error || "Failed to fetch exam purchases", false);
    }
  }, [
    page,
    limit,
    debouncedSearch,
    sort,
    paymentStatus,
    fulfillmentStatus,
    emailStatus,
    startDate,
    endDate,
    fetchExamPurchases,
    showToast,
  ]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // Fetch the dashboard for the selected exam type. `fetchExamDashboard`
  // renders any cached snapshot instantly and then always revalidates, so the
  // card never shows stale numbers.
  useEffect(() => {
    fetchExamDashboard({
      recentPurchasesLimit: 10,
      recentFailedEmailsLimit: 10,
      examType: dashboardExamType,
    });
  }, [fetchExamDashboard, dashboardExamType]);

  // Fetch stats when the date range changes.
  useEffect(() => {
    fetchExamPurchaseStats({
      startDate: toIsoStart(startDate),
      endDate: toIsoEnd(endDate),
    });
  }, [fetchExamPurchaseStats, startDate, endDate]);

  const buildParams = () => {
    const p = { page: String(page), limit: String(limit) };
    if (debouncedSearch) p.search = debouncedSearch;
    if (sort) p.sort = sort;
    if (paymentStatus) p.paymentStatus = paymentStatus;
    if (fulfillmentStatus) p.fulfillmentStatus = fulfillmentStatus;
    if (emailStatus) p.emailStatus = emailStatus;
    if (startDate) p.startDate = startDate;
    if (endDate) p.endDate = endDate;
    return p;
  };

    const handlePageChange = (newPage) => {
    const params = buildParams();
    params.page = String(newPage);
    setSearchParams(params);
  };

  const handleSearchSubmit = () => {
    const p = { page: "1", limit: String(limit) };
    if (debouncedSearch) p.search = debouncedSearch;
    if (sort) p.sort = sort;
    if (paymentStatus) p.paymentStatus = paymentStatus;
    if (fulfillmentStatus) p.fulfillmentStatus = fulfillmentStatus;
    if (emailStatus) p.emailStatus = emailStatus;
    if (startDate) p.startDate = startDate;
    if (endDate) p.endDate = endDate;
    setSearchParams(p);
  };

  const handleView = (purchase) => {
    setSelectedPurchaseId(purchase.id);
    setViewModalOpen(true);
  };

  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedPurchaseId(null);
  };

  const handleRetry = async (purchase) => {
    const result = await retryExamPurchaseFulfillment(purchase.id);
    if (result.success) {
      showToast("Fulfillment retried successfully", true);
      fetchPurchases();
    } else {
      showToast(result.error || "Failed to retry fulfillment", false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setPaymentStatus("");
    setFulfillmentStatus("");
    setEmailStatus("");
    setStartDate("");
    setEndDate("");
    setSort("newest");
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNavBar
        title="Exam Purchases"
        subTitle="Manage exam token/pin purchases"
        context={`${total || 0} purchases found`}
      />

      <AdminPageBody>
        <ExamSystemAlerts alerts={examDashboard?.alerts} />

        {examPurchaseStats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Total", examPurchaseStats.totalPurchases],
              ["Completed", examPurchaseStats.payments?.completed],
              ["Pending", examPurchaseStats.payments?.pending],
              ["Failed", examPurchaseStats.payments?.failed],
              ["Fulfilled", examPurchaseStats.fulfillment?.fulfilled],
              ["Emails sent", examPurchaseStats.emails?.sent],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm"
              >
                <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {label}
                </p>
                <p className="m-0 text-lg font-bold text-slate-900">
                  {examPurchaseStatsLoading ? "…" : value ?? 0}
                </p>
              </div>
            ))}
          </div>
        )}

        <ExamPurchaseFilters
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearchSubmit}
          paymentStatus={paymentStatus}
          onPaymentStatusChange={setPaymentStatus}
          fulfillmentStatus={fulfillmentStatus}
          onFulfillmentStatusChange={setFulfillmentStatus}
          emailStatus={emailStatus}
          onEmailStatusChange={setEmailStatus}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          sort={sort}
          onSortChange={setSort}
          onResetFilters={resetFilters}
        />

        <section className="mb-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xs font-bold uppercase tracking-wide text-slate-700">Overview</h2>
            <div className="flex items-center gap-2">
              <label
                htmlFor="dashboardExamType"
                className="text-[11px] font-semibold uppercase tracking-wide text-slate-500"
              >
                Exam Type
              </label>
              <select
                id="dashboardExamType"
                className="form-select form-select-sm"
                style={{ minWidth: "140px" }}
                value={dashboardExamType}
                onChange={(e) => setDashboardExamType(e.target.value)}
              >
                <option value="">All Exam Types</option>
                {EXAM_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Stats only — the recent lists render below the purchases table. */}
          <ExamDashboardView
            dashboard={examDashboard}
            loading={dashboardLoading}
            showRecent={false}
          />
        </section>

        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {examPurchases.length} of {total} purchases (page {page} of {totalPages || 1})
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-6">
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100"></div>
                ))}
              </div>
            </div>
          ) : examPurchases.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No exam purchases found. Try adjusting your filters.
            </div>
          ) : (
            <ExamPurchaseTable
              purchases={examPurchases}
              isReloading={loading}
              sort={sort}
              onSortChange={setSort}
              onView={handleView}
              onRetry={handleRetry}
              retryLoadingId={retryLoading ? selectedPurchaseId : null}
            />
          )}
        </div>

        {!loading && totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <ExamPagination page={page} lastPage={totalPages} onPageChange={handlePageChange} />
          </div>
        )}

        {/* Recent activity — after the table, so the purchases list comes first */}
        <section className="space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-700">
            Recent Activity
          </h2>
          <ExamRecentActivity dashboard={examDashboard} />
        </section>
      </AdminPageBody>

      <ExamPurchaseDetailModal
        isOpen={viewModalOpen}
        onClose={handleViewModalClose}
        purchaseId={selectedPurchaseId}
        onRetry={handleRetry}
        retryLoading={retryLoading}
      />

      {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}
    </div>
  );
};

export default ExamPurchases;